import type { AppRouter } from "@repo/vihangam-yog-backend/router";
import { rosterDisplayName } from "@repo/vihangam-yog-shared/roster";
import type { inferRouterOutputs } from "@trpc/server";

import { CHAPTER } from "../../chapter";

import {
  ONE_OFF_EVENT_SLUG,
  ONE_OFF_EVENT_TITLE,
  ORGANISATION_EMAIL_TYPES,
} from "./emailTypes";
import { people } from "./sendCopy";

/** One logged send to everyone, newest first from adminListEmailSends. */
export type EmailSend =
  inferRouterOutputs<AppRouter>["adminListEmailSends"][number];

const MONTH = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

/** "12 Sep, 7:40 pm" in this build's time zone. */
export function formatSentAt(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return "an unknown time";
  }
  const parts = new Intl.DateTimeFormat("en-AU", {
    timeZone: CHAPTER.timezone,
    day: "numeric",
    month: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  }).formatToParts(date);
  const part = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((p) => p.type === type)?.value ?? "";
  const month = MONTH[Number(part("month")) - 1] ?? "";
  return `${part("day")} ${month}, ${part("hour")}:${part("minute")} ${part("dayPeriod").toLowerCase()}`;
}

/** The sender by name when they're on the team roster, else their email;
 *  null when the send didn't record who made it. */
export function senderNameOf(send: EmailSend): string | null {
  return rosterDisplayName(send.sentBy) ?? send.sentBy ?? null;
}

/** " by Shekher", or nothing when the sender wasn't recorded. */
function sentByPhrase(send: EmailSend): string {
  const name = senderNameOf(send);
  return name ? ` by ${name}` : "";
}

/** The event a send was for, as the page names it. */
export function eventTitleOf(send: EmailSend): string {
  return send.eventSlug === ONE_OFF_EVENT_SLUG
    ? ONE_OFF_EVENT_TITLE
    : send.eventTitle;
}

/** "14 people" or "79 organisations", for who a logged send went to. */
export function recipientsOf(send: EmailSend, count: number): string {
  if (ORGANISATION_EMAIL_TYPES.includes(send.type)) {
    return `${count} ${count === 1 ? "organisation" : "organisations"}`;
  }
  return people(count);
}

/** The newest send of any of these types for one event. */
export function latestSend(
  history: readonly EmailSend[],
  eventSlug: string,
  types: readonly string[],
): EmailSend | undefined {
  return history.find(
    (send) => send.eventSlug === eventSlug && types.includes(send.type),
  );
}

/**
 * "Last sent 12 Sep, 7:40 pm by Shekher, to 14 people" or "Not sent yet".
 * Null while the history is still loading or failed to load, so the page
 * never says "Not sent yet" about an email it can't see.
 */
export function lastSentLine(
  history: readonly EmailSend[] | undefined,
  eventSlug: string,
  types: readonly string[],
): string | null {
  if (!history) {
    return null;
  }
  const send = latestSend(history, eventSlug, types);
  return send
    ? `Last sent ${formatSentAt(send.createdAt)}${sentByPhrase(send)}, to ${recipientsOf(send, send.sent)}`
    : "Not sent yet";
}

/** The extra line in a confirm when this email has gone out before. */
export function alreadySentNote(
  history: readonly EmailSend[] | undefined,
  eventSlug: string,
  types: readonly string[],
): string | null {
  const send = history ? latestSend(history, eventSlug, types) : undefined;
  return send
    ? `This email already went to ${recipientsOf(send, send.sent)} on ${formatSentAt(send.createdAt)}.`
    : null;
}
